import { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { CheckCircleOutline } from '@mui/icons-material';

import { Text } from '../../../shared/uiComponents';
import { GREEN } from '../../../shared/Helpers/colors';
import VoiceTextInput, { FollowUpQuestion } from './VoiceTextInput';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { checkSoapNoteCoverage, parseSoapNoteWithAi } from '../../../redux/State/clientSlice/soapNoteAiSlice';
import { AiParseSoapNoteResponse } from '../../../redux/API/ClientAPIHelpers/ai';

import './styles.scss';

// Required checklist points the BT must cover per session, per LG-1261 Part 2.
// Each entry is hidden once its fieldKey is already filled on the draft (per getCurrentFieldValues),
// so the BT is only prompted for topics that genuinely still need to be addressed.
// Kept one fieldKey per entry (rather than grouping e.g. mood + response to treatment together) —
// coverage-check only marks a compound topic "covered" once EVERY one of its fieldKeys is
// substantively mentioned, so a grouped item re-asks the whole combined question even when the BT
// already covered part of it (e.g. saying "client was very happy" doesn't cover a combined
// mood-and-response-to-treatment topic, so the full question gets re-asked as if mood was never said).
const CHECKLIST_DEFINITIONS: Array<FollowUpQuestion & { fieldKeys: string[] }> = [
	{ id: 'mood', label: 'Client\'s mood', fieldKeys: ['clientMood'] },
	{ id: 'responseToTreatment', label: 'Response to treatment', fieldKeys: ['clientResponseToTreatment'] },
	{ id: 'futurePlan', label: 'Plan for next session', fieldKeys: ['planForFutureSession'] },
	{ id: 'maladaptiveBehaviorSeverity', label: 'Behavior severity', fieldKeys: ['levelOfMaladaptiveBehavior'] },
	{ id: 'barriers', label: 'Barriers or context', fieldKeys: ['barriersToTreatment'] },
	{ id: 'barrierImpact', label: 'Barrier impact', fieldKeys: ['howBarrierAffectedSession'] },
];

interface AIAssistSoapNoteProperties {
	clientId: string;
	disabled?: boolean;
	getCurrentFieldValues?: () => Record<string, string>;
	onFieldsExtracted: (fields: AiParseSoapNoteResponse) => void;
	replaceableHardDataFields?: string[];
	reportId?: string;
	sessionId: string;
}

const AIAssistSoapNote: FC<AIAssistSoapNoteProperties> = ({
	clientId,
	sessionId,
	onFieldsExtracted,
	disabled = false,
	getCurrentFieldValues,
	replaceableHardDataFields = [],
	reportId,
}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState('');

	const behaviorTreatmentHardData = useSelector((state) => state.soapNote.behaviorTreatmentHardData);
	const clientMoodList = useSelector((state) => state.soapNote.behaviorTreatmentOptions.clientMoodOptions);
	const clientResponseToTreatmentList = useSelector(
		(state) => state.soapNote.behaviorTreatmentOptions.clientResponseToTreatmentOptions,
	);
	const behaviorTreatmentFutureList = useSelector(
		(state) => state.soapNote.behaviorTreatmentOptions.behaviorTreatmentFuturePlanOptions,
	);
	const levelOfMaladaptiveBehaviorList = useSelector(
		(state) => state.soapNote.behaviorTreatmentOptions.levelOfMaladaptiveBehaviorOptions,
	);
	const barriersList = useSelector((state) => state.soapNote.behaviorTreatmentOptions.barrierToTreatmentOptions);
	const technuquiesList = useSelector(
		(state) => state.soapNote.behaviorTreatmentOptions.behaviorTreatmentTechniqueOptions,
	);
	const maladaptiveBehaviorTechniquesList = useSelector(
		(state) => state.soapNote.behaviorTreatmentOptions.maladaptiveBehaviorTechniqueOptions,
	);
	const participantsList = useSelector(
		(state) => state.soapNote.behaviorTreatmentOptions.behaviorTreatmentParticipantOptions,
	);
	const reinforcerList = useSelector((state) => state.soapNote.behaviorTreatmentOptions.reinforcementOptions);
	const skillDomainAddressedList = useSelector(
		(state) => state.soapNote.behaviorTreatmentOptions.skillDomainAddressedOptions,
	);

	const buildParseRequest = useCallback(
		({
			prompt,
			currentFieldValues,
			isFollowUp,
		}: {
			currentFieldValues: Record<string, string>;
			isFollowUp: boolean;
			prompt: string;
		}) => ({
			prompt,
			clientId,
			sessionId,
			reportId,
			clientMoodOptions: clientMoodList.map((x) => x.text),
			clientResponseToTreatmentOptions: clientResponseToTreatmentList.map((x) => x.text),
			futurePlanOptions: behaviorTreatmentFutureList.map((x) => x.text),
			levelOfMaladaptiveBehaviorOptions: levelOfMaladaptiveBehaviorList.map((x) => x.text),
			barrierToTreatmentOptions: barriersList.map((x) => x.text),
			behaviorTreatmentTechniqueOptions: technuquiesList.map((x) => x.text),
			maladaptiveBehaviorTechniqueOptions: maladaptiveBehaviorTechniquesList.map((x) => x.text),
			participantOptions: participantsList.map((x) => x.text),
			reinforcerOptions: reinforcerList.map((x) => x.text),
			reinforcerHardData: behaviorTreatmentHardData?.reinforcementTitles || [],
			skillDomainAddressedOptions: skillDomainAddressedList.map((x) => x.text),
			skillDomainAddressedHardData: behaviorTreatmentHardData?.skillDomainNames || [],
			workedGoalsHardData: behaviorTreatmentHardData?.workedGoals,
			frequencyAndDurationOfMaladaptiveBehaviorsHardData:
				behaviorTreatmentHardData?.frequencyAndDurationOfMaladaptiveBehaviors,
			replaceableHardDataFields,
			currentFieldValues,
			isFollowUp,
		}),
		[
			behaviorTreatmentHardData,
			clientId,
			sessionId,
			reportId,
			clientMoodList,
			clientResponseToTreatmentList,
			behaviorTreatmentFutureList,
			levelOfMaladaptiveBehaviorList,
			barriersList,
			technuquiesList,
			maladaptiveBehaviorTechniquesList,
			participantsList,
			reinforcerList,
			replaceableHardDataFields,
			skillDomainAddressedList,
		],
	);

	const handleVoiceTextSubmit = useCallback(
		async (text: string) => {
			setLoading(true);
			setError('');
			setSuccess(false);

			const currentFieldValues = getCurrentFieldValues?.() || {};
			const isFollowUp = Object.keys(currentFieldValues).length > 0;

			// Follow-up parsing only sends transcript-derived draft fields; deterministic hard data is excluded.
			try {
				const result = await dispatch(
					parseSoapNoteWithAi(
						buildParseRequest({
							prompt: text,
							currentFieldValues,
							isFollowUp,
						}),
					),
				).then(unwrapResult);

				onFieldsExtracted(result);
				setSuccess(true);
			} catch (e) {
				setError(
					e instanceof Error
						? e.message
						: (e as { title?: string })?.title || 'Failed to parse the session summary. Please try again.',
				);

				throw e;
			} finally {
				setLoading(false);
			}
		},
		[
			dispatch,
			buildParseRequest,
			getCurrentFieldValues,
			onFieldsExtracted,
		],
	);

	const checklistItems = useMemo(() => {
		const currentValues = getCurrentFieldValues?.() || {};

		return CHECKLIST_DEFINITIONS.filter((item) =>
			item.fieldKeys.some((key) => !currentValues[key]?.trim()),
		).map(({ id, label }) => ({ id, label }));
	}, [getCurrentFieldValues]);

	// Checklist coverage check — called once, when the BT taps Done (see VoiceTextInput.handleDone),
	// to decide which checklist points still need a follow-up question. Uses a lightweight, dedicated
	// endpoint (not the full parse-soap-note extraction) since it only needs checklist coverage, not
	// full field extraction — see LG-1261 Part 2 cost optimization. Does NOT apply any values to the
	// draft form; only the final submit (handleVoiceTextSubmit) does that.
	const checkCoverage = useCallback(
		async (transcriptSoFar: string): Promise<Set<string>> => {
			if (!transcriptSoFar.trim() || !checklistItems.length) return new Set();

			try {
				const result = await dispatch(
					checkSoapNoteCoverage({
						transcriptSoFar,
						clientId,
						checklistItems: checklistItems.map(({ id, label }) => ({ id, label })),
					}),
				).then(unwrapResult);

				return new Set(result.coveredIds || []);
			} catch {
				return new Set();
			}
		},
		[dispatch, clientId, checklistItems],
	);

	return (
		<div className="aiAssistSoapNote">
			<div className="aiAssistSoapNote__inputSection">
				<VoiceTextInput
					onSubmit={handleVoiceTextSubmit}
					onCheckCoverage={checkCoverage}
					loading={loading}
					disabled={disabled}
					checklistItems={checklistItems}
				/>

				{success && (
					<div className="aiAssistSoapNote__success">
						<CheckCircleOutline fontSize="small" style={{ color: GREEN.BASE }} />
						<Text
							title="Fields filled from AI. Review and adjust below."
							type="psm"
							color={GREEN.BASE}
						/>
					</div>
				)}

				{Boolean(error) && (
					<div className="aiAssistSoapNote__error">
						<Text title={error} type="psm" color="rgba(214, 69, 38, 1)" />
					</div>
				)}
			</div>
		</div>
	);
};

export default AIAssistSoapNote;
